import { Component, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { ToolReturnsService } from "@services/tool-returns.service";
import { ActivatedRoute, Router } from "@angular/router";
import { Location } from '@angular/common';
import { ToastsManager } from "@app/shared/util/toastr.service";
import { TranslateService } from "@ngx-translate/core";
import { Log } from "ng2-logger";
import { Tool } from "@app/tool/model/tool.model";
import { ToolReturn } from "@models/tool-return.model";
import { ToolRequestStatus } from "@app/tool/model/enums/tool-request-status.enum";
import { SettingsType } from "@app/settings/models/settingsType.model";
import {TreeModel} from "@components/tree/tree.model";
import {ToolSet} from "@app/tool/model/toolSet.model";

@Component({
    selector: 'app-tool-returns-facilities-inspect',
    templateUrl: './tool-returns-facilities-inspect.component.html',
    styleUrls: ['./tool-returns-facilities-inspect.component.scss']
})
export class ToolReturnsFacilitiesInspectComponent implements OnInit {
    static returnTools: Array<Tool | ToolSet> = [];
    _logger = Log.create(ToolReturnsFacilitiesInspectComponent.name);

    public toolReturn: ToolReturn = new ToolReturn();
    public hasSeveralReturnOptions: boolean;

    inspectForm: UntypedFormGroup;
    showValidations = false;
    hasConfirmationInProgress = false;
    toolScanActive = false;

    static scannedToolsValidator(control: UntypedFormControl): { [s: string]: boolean } {
        const nrOfToolsFromReturn =
            ToolReturnsFacilitiesInspectComponent.returnTools.reduce((sum, item) => {
                return sum + ('nrOfTools' in item ? Number(item.nrOfTools) : 1);
            }, 0);

        const nrOfToolsFromControls = control.value.filter(
            (item: TreeModel) => item.children.length === 0).length;

        if (!control.value.length || nrOfToolsFromControls < nrOfToolsFromReturn) {
            return {'allToolsInspected': true};
        }

        return null;
    }

    constructor(private toolReturnsService: ToolReturnsService,
                private router: Router,
                private route: ActivatedRoute,
                private location: Location,
                private toastr: ToastsManager,
                private translationService: TranslateService) {
                this.hasSeveralReturnOptions = this.toolReturn.hasSeveralReturnOptions || false;
    }

    ngOnInit() {
        this.onFormInit();
        ToolReturnsFacilitiesInspectComponent.returnTools = [];
        this.setToolScan();
        this.route.params.subscribe(params => {
            this.getToolReturn(params['id']);
        });
    }

    getToolReturn(id) {
        this.toolReturnsService.getToolReturn(id).subscribe(
            (data) => {
                this._logger.info('ngOnInit:: API Returned data', data);
                if (!data || (data.status !== ToolRequestStatus.DELIVERED && data.status !== ToolRequestStatus.WAITING_FOR_APPROVAL)) {
                    this.router.navigate([`tool-returns-facilities`]);
                    return;
                }
                this.toolReturn = data;
                ToolReturnsFacilitiesInspectComponent.returnTools = data.returnTools;
            },
            (error) => {
                this._logger.info('ngOnInit:: API Returned error', error);
                this.router.navigate([`tool-returns-facilities`]);
            }
        );
    }

    setToolScan() {
        this.toolReturnsService.getSettings().subscribe(
            (settings) => {
                if (settings && settings.length > 0 && this.inspectForm) {
                    this.toolScanActive = settings.find((item) =>
                                item.type === SettingsType.ToolScanOnServiceReturn)
                                .value === 'true';

                    if (!this.toolScanActive) {
                        this.inspectForm.get('scannedTools').clearValidators();
                        this.inspectForm.get('scannedTools').updateValueAndValidity({emitEvent: false});
                    } else {
                        this.inspectForm.get('scannedTools')
                            .setValidators([ToolReturnsFacilitiesInspectComponent.scannedToolsValidator.bind(this)]);
                        this.inspectForm.get('scannedTools').updateValueAndValidity({emitEvent: false});
                    }
                }
            }
        )
    }

    onFormInit() {
        this.inspectForm = new UntypedFormGroup({
            'scannedTools': new UntypedFormControl([], ToolReturnsFacilitiesInspectComponent.scannedToolsValidator.bind(this)),
        });

        this._logger.info('onFormInit:: Form initialised', this.inspectForm);
    }

    approveToolReturn() {
        if (this.inspectForm.invalid) {
            this._logger.info('approveToolReturn:: Form is invalid', this.inspectForm);
            this.showValidations = true;
            return;
        }

        this._logger.info("approveToolReturn:: Approving tool return");
        this.showValidations = false;
        this.hasConfirmationInProgress = true;
        this.toolReturnsService.approveToolReturn(this.toolReturn, false).subscribe(
            (data: any) => {
                this._logger.info("approveToolReturn:: API returned data", data);
                this.toastr.success(this.buildApproveMessage(this.toolReturn.id));
                this.router.navigate([`tool-returns-facilities`]);
                this.hasConfirmationInProgress = false;
            },
            (error: any) => {
                this._logger.error("approveToolReturn:: API returned error", error);
                this.hasConfirmationInProgress = false;
            }
        );
    }

    approveToolReturnForLine() {
        if (this.inspectForm.invalid) {
            this._logger.info('approveToolReturn:: Form is invalid', this.inspectForm);
            this.showValidations = true;
            return;
        }

        this._logger.info("approveToolReturn:: Approving tool return");
        this.showValidations = false;
        this.hasConfirmationInProgress = true;
        this.toolReturnsService.approveToolReturn(this.toolReturn, true).subscribe(
            (data: any) => {
                this._logger.info("approveToolReturn:: API returned data", data);
                this.toastr.success(this.buildApproveMessage(this.toolReturn.id));
                this.router.navigate([`tool-returns-facilities`]);
                this.hasConfirmationInProgress = false;
            },
            (error: any) => {
                this._logger.error("approveToolReturn:: API returned error", error);
                this.hasConfirmationInProgress = false;
            }
        );
    }

    onToolScanned(tool) {
        this.toolReturnsService.confirmToolScan(this.toolReturn, tool).subscribe(
            (data: any) => {
                this.getToolReturn(this.toolReturn.id);
                this._logger.info("confirmToolScan:: API returned data", data);
            },
            (error: any) => {
                this._logger.error("confirmToolScan:: API returned error", error);
            }
        );
    }

    onToolsScanned(tools) {
        this._logger.info("onToolsScanned:: Scanned tools", tools);
        this.inspectForm.controls['scannedTools'].setValue(tools);
    }

    onRowAlertClick(event) {
        this._logger.info("onRowAlertClick:: ", event);
    }

    buildApproveMessage(toolReturnId) {
        return this.translationService.instant('atom.tool-returns.toasts.approved',
            {toolReturnId: toolReturnId});
    }

    onBackPressed() {
        this.location.back();
    }

    onItemNotFound(event: any) {
        this.toastr.warning(this.buildToolNotRequiredMessage(event));
    }

    private buildToolNotRequiredMessage(uid) {
        return this.translationService.instant('tree.toolSelect.toasts.notRequired', {uid: uid});
    }
}
